/**
 * Settings — brand, notifications, developer mode, support, account.
 *
 * Money routing (settlement, receive chains, pay-in coins) lives in the
 * Coins tab; this screen is just account/profile preferences.
 */

import { useEffect, useState } from "react";
import {
  ApiError,
  adminFetch,
  deleteMerchantLogo,
  getMyMerchant,
  uploadMerchantLogo,
  upsertMyMerchant,
  type MerchantProfile,
} from "../lib/api";

const API_BASE: string =
  (import.meta.env.VITE_SHIELDZ_API_URL as string | undefined) ?? "https://shieldz.cash";
const resolveLogoUrl = (u: string): string =>
  u.startsWith("/") ? `${API_BASE.replace(/\/+$/, "")}${u}` : u;
import { Banner, Btn, CopyField, KV, Toggle } from "../components/ui";
import { NotificationsCard } from "../components/NotificationsCard";
import { Ic } from "../components/Icons";
import {
  importKeystore,
  deriveEvmAddress,
  exportV3Keystore,
  downloadJson,
  type Keystore,
} from "../lib/key-gen";
import { isDeveloperMode, setDeveloperMode } from "../lib/developer-mode";
import { signOut, getStoredSession } from "../lib/session";
import { useNavigate } from "@tanstack/react-router";
import { useT } from "../lib/i18n";

export function Settings() {
  const t = useT();
  const navigate = useNavigate();
  const [merchant, setMerchant] = useState<MerchantProfile | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [devMode, setDevModeLocal] = useState(isDeveloperMode());
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [accent, setAccent] = useState("");
  // Effective preview color, mirrors the checkout's default emerald when unset.
  const pvAccent = /^#[0-9a-fA-F]{6}$/.test(accent) ? accent : "#06845e";

  // Backup & recovery — unlock the Shieldz keystore in-browser and export to
  // standard formats. The mnemonic stays in memory on this page only.
  const [bkFile, setBkFile] = useState<Keystore | null>(null);
  const [bkFileName, setBkFileName] = useState("");
  const [bkPass, setBkPass] = useState("");
  const [bkMnemonic, setBkMnemonic] = useState<string | null>(null);
  const [bkAddr, setBkAddr] = useState("");
  const [bkErr, setBkErr] = useState<string | null>(null);
  const [bkBusy, setBkBusy] = useState(false);
  const [bkReveal, setBkReveal] = useState(false);
  const [bkV3Pass, setBkV3Pass] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = await getMyMerchant();
        if (cancelled) return;
        setMerchant(m);
        setDisplayName(m.display_name ?? "");
        setLogoUrl(m.logo_url ?? null);
        setAccent(m.accent ?? "");
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    setError(null);
    setBusy(true);
    try {
      await upsertMyMerchant({ display_name: displayName.trim(), accent: accent || null });
      setSavedAt(Date.now());
      setMerchant(await getMyMerchant());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onPickLogo = async (file: File | null) => {
    if (!file) return;
    setError(null);
    setLogoBusy(true);
    try {
      const r = await uploadMerchantLogo(file);
      setLogoUrl(r.logo_url);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLogoBusy(false);
    }
  };

  const onRemoveLogo = async () => {
    setError(null);
    setLogoBusy(true);
    try {
      await deleteMerchantLogo();
      setLogoUrl(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLogoBusy(false);
    }
  };

  // ── Backup & recovery ──────────────────────────────────────────────
  const bkPickFile = async (file: File | null) => {
    setBkErr(null);
    if (!file) { setBkFile(null); setBkFileName(""); return; }
    try {
      const blob = JSON.parse(await file.text()) as Keystore;
      if (typeof blob?.ct_b64 !== "string" || typeof blob?.version !== "number") throw new Error("bad");
      setBkFile(blob);
      setBkFileName(file.name);
    } catch {
      setBkFile(null); setBkFileName("");
      setBkErr(t("settings.bk_bad_file"));
    }
  };

  const bkUnlock = async () => {
    if (!bkFile) return;
    setBkErr(null);
    setBkBusy(true);
    try {
      const m = await importKeystore(bkFile, bkPass);
      setBkMnemonic(m);
      setBkAddr(deriveEvmAddress(m));
      setBkPass("");
    } catch {
      setBkErr(t("settings.bk_wrong_pass"));
    } finally {
      setBkBusy(false);
    }
  };

  const bkDownloadV3 = async () => {
    if (!bkMnemonic) return;
    setBkErr(null);
    setBkBusy(true);
    try {
      const v3 = await exportV3Keystore(bkMnemonic, bkV3Pass);
      downloadJson(v3, `metamask-keystore-${bkAddr.slice(0, 6)}.json`);
    } catch (e) {
      setBkErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBkBusy(false);
    }
  };

  const bkLock = () => {
    setBkMnemonic(null);
    setBkReveal(false);
    setBkV3Pass("");
    setBkAddr("");
    setBkFile(null);
    setBkFileName("");
  };

  if (!merchant) {
    return (
      <div className="scroll">
        <div className="pad">
          <div className="muted" style={{ color: "var(--ink-3)" }}>{t("settings.loading")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="scroll">
      <div className="pad fade-in">
        {error && (
          <div style={{ marginBottom: 14 }}>
            <Banner tone="fail">{error}</Banner>
          </div>
        )}
        {savedAt && (
          <div style={{ marginBottom: 14 }}>
            <Banner tone="ok">{t("settings.saved_at")} {new Date(savedAt).toLocaleTimeString()}</Banner>
          </div>
        )}

        <div className="s-label">{t("settings.brand_heading")}</div>
        <div className="card card-pad">
          <div className="field">
            <label htmlFor="dn">{t("settings.display_name_label")}</label>
            <input
              id="dn"
              className="input"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={120}
            />
            <div className="help">
              {t("settings.display_name_help")}
            </div>
          </div>

          <div className="field" style={{ marginTop: 14 }}>
            <label>{t("settings.logo_label")}</label>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 4 }}>
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 14,
                  flex: "none",
                  display: "grid",
                  placeItems: "center",
                  overflow: "hidden",
                  background: logoUrl ? "var(--surface)" : "var(--brand-50)",
                  color: "var(--brand-700)",
                  border: "1px solid var(--line-2)",
                  fontWeight: 700,
                }}
              >
                {logoUrl ? (
                  <img
                    src={resolveLogoUrl(logoUrl)}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  (displayName.trim().charAt(0) || "S").toUpperCase()
                )}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <label className="btn btn-secondary" style={{ cursor: "pointer" }}>
                  {logoBusy ? t("settings.logo_uploading") : logoUrl ? t("settings.logo_replace") : t("settings.logo_upload")}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    style={{ display: "none" }}
                    onChange={(e) => void onPickLogo(e.target.files?.[0] ?? null)}
                  />
                </label>
                {logoUrl && (
                  <Btn variant="ghost" onClick={() => void onRemoveLogo()} loading={logoBusy}>
                    {t("settings.logo_remove")}
                  </Btn>
                )}
              </div>
            </div>
            <div className="help" style={{ marginTop: 8 }}>
              {t("settings.logo_help")}
            </div>
          </div>

          <div className="field" style={{ marginTop: 18 }}>
            <label>{t("settings.accent_color_label")}</label>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(accent) ? accent : "#06845e"}
                onChange={(e) => setAccent(e.target.value)}
                aria-label={t("settings.accent_color_label")}
                style={{
                  width: 46,
                  height: 46,
                  padding: 4,
                  borderRadius: "var(--r-md)",
                  border: "1px solid var(--line-2)",
                  background: "var(--surface)",
                  cursor: "pointer",
                }}
              />
              <input
                className="input"
                value={accent}
                placeholder="#06845e"
                spellCheck={false}
                maxLength={7}
                onChange={(e) => setAccent(e.target.value.trim())}
                style={{ maxWidth: 150, fontFamily: "var(--font-mono, ui-monospace, monospace)" }}
              />
              {accent && (
                <Btn variant="ghost" onClick={() => setAccent("")}>
                  {t("settings.accent_reset")}
                </Btn>
              )}
            </div>
            <div className="help" style={{ marginTop: 8 }}>
              {t("settings.accent_help")}
            </div>
          </div>

          <div className="field" style={{ marginTop: 18 }}>
            <label>{t("settings.checkout_preview_label")}</label>
            <div
              style={{
                borderRadius: 16,
                border: "1px solid var(--line-2)",
                background: "#fafbfc",
                padding: "16px 16px 14px",
                maxWidth: 340,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    flex: "0 0 auto",
                    background: logoUrl ? "#fff" : pvAccent,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 15,
                    overflow: "hidden",
                    boxShadow: "inset 0 0 0 1px rgba(0,0,0,.06)",
                  }}
                >
                  {logoUrl ? (
                    <img
                      src={resolveLogoUrl(logoUrl)}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    (displayName.trim().charAt(0) || "S").toUpperCase()
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 650, fontSize: 14, color: "#0a0b0d" }}>
                    {displayName.trim() || t("settings.preview_your_brand")}
                  </div>
                  <div style={{ fontSize: 11.5, color: "#8a919e" }}>{t("settings.preview_secure_checkout")}</div>
                </div>
              </div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: ".06em",
                  textTransform: "uppercase",
                  color: "#8a919e",
                }}
              >
                {t("settings.preview_amount_due")}
              </div>
              <div
                style={{
                  fontSize: 30,
                  fontWeight: 600,
                  letterSpacing: "-.02em",
                  color: "#0a0b0d",
                  marginBottom: 12,
                  fontFamily: "var(--font-mono, ui-monospace, monospace)",
                }}
              >
                $49.00
              </div>
              <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
                {["USDC", "USDT", "ETH"].map((c) => (
                  <span
                    key={c}
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      padding: "5px 10px",
                      borderRadius: 999,
                      background: `color-mix(in srgb, ${pvAccent} 10%, #fff)`,
                      color: `color-mix(in srgb, ${pvAccent} 78%, #000)`,
                      border: `1px solid color-mix(in srgb, ${pvAccent} 22%, #fff)`,
                    }}
                  >
                    {c}
                  </span>
                ))}
              </div>
              <div
                style={{
                  textAlign: "center",
                  padding: "12px",
                  borderRadius: 12,
                  background: pvAccent,
                  color: "#fff",
                  fontWeight: 650,
                  fontSize: 14,
                }}
              >
                {t("settings.preview_pay_with_wallet")}
              </div>
              <div style={{ textAlign: "center", fontSize: 10.5, color: "#b6bbc4", marginTop: 10 }}>
                {t("settings.preview_powered_by")}
              </div>
            </div>
            <div className="help" style={{ marginTop: 8 }}>
              {t("settings.preview_help")}
            </div>
          </div>
        </div>

        <div className="s-label">{t("settings.money_coins_heading")}</div>
        <div className="card card-pad">
          <p className="help" style={{ marginBottom: 12 }}>
            {t("settings.money_coins_help")}
          </p>
          <Btn variant="secondary" onClick={() => void navigate({ to: "/coins" })}>
            {t("settings.open_coins")}
          </Btn>
        </div>

        <div className="s-label">{t("settings.backup_recovery_heading")}</div>
        <div className="card card-pad">
          <p className="help" style={{ marginBottom: 12 }}>
            {t("settings.backup_recovery_help")}
          </p>

          {bkErr && (
            <div style={{ marginBottom: 12 }}>
              <Banner tone="fail">{bkErr}</Banner>
            </div>
          )}

          {!bkMnemonic ? (
            <>
              <div className="field">
                <label htmlFor="bk-file">{t("settings.keystore_file_label")}</label>
                <label
                  htmlFor="bk-file"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    padding: "11px 14px", border: "1px solid var(--line-2)",
                    borderRadius: 13, background: "var(--surface)", cursor: "pointer",
                    fontSize: 13.5, fontWeight: 500, color: "var(--ink-2)", width: "fit-content",
                  }}
                >
                  <Ic name="key" size={15} />
                  {bkFileName || t("settings.choose_keystore")}
                  <input
                    id="bk-file" type="file" accept="application/json,.json"
                    style={{ display: "none" }}
                    onChange={(e) => void bkPickFile(e.target.files?.[0] ?? null)}
                  />
                </label>
              </div>
              <div className="field" style={{ marginTop: 12 }}>
                <label htmlFor="bk-pass">{t("settings.keystore_passphrase_label")}</label>
                <input
                  id="bk-pass" className="input" type="password" autoComplete="off"
                  spellCheck={false} placeholder="••••••••" value={bkPass}
                  onChange={(e) => setBkPass(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && bkFile && bkPass.length >= 8) void bkUnlock(); }}
                />
              </div>
              <div className="row" style={{ marginTop: 12 }}>
                <Btn onClick={() => void bkUnlock()} disabled={bkBusy || !bkFile || bkPass.length < 8}>
                  {bkBusy ? t("settings.unlocking") : t("settings.unlock")}
                </Btn>
              </div>
            </>
          ) : (
            <>
              <div className="ac-note" style={{ marginTop: 0 }}>
                <Ic name="check" size={15} className="ic" />
                <div>{t("settings.unlocked")} <span className="mono">{bkAddr}</span></div>
              </div>

              <div className="set-label" style={{ marginTop: 16 }}>{t("settings.recovery_phrase_label")}</div>
              {!bkReveal ? (
                <Btn variant="secondary" onClick={() => setBkReveal(true)}>{t("settings.reveal_12_words")}</Btn>
              ) : (
                <>
                  <pre
                    className="mono"
                    style={{
                      margin: "0 0 6px", padding: 12, background: "var(--surface-2)",
                      border: "1px solid var(--line)", borderRadius: 10, fontSize: 13,
                      lineHeight: 1.7, whiteSpace: "pre-wrap",
                    }}
                  >{bkMnemonic}</pre>
                  <div className="help">
                    {t("settings.recovery_phrase_help")}
                  </div>
                </>
              )}

              <div className="set-label" style={{ marginTop: 18 }}>{t("settings.metamask_v3_label")}</div>
              <div className="field">
                <label htmlFor="bk-v3">{t("settings.v3_password_label")}</label>
                <input
                  id="bk-v3" className="input" type="password" autoComplete="off"
                  spellCheck={false} placeholder="at least 8 characters" value={bkV3Pass}
                  onChange={(e) => setBkV3Pass(e.target.value)}
                />
                <div className="help">
                  {t("settings.v3_help")} ({bkAddr.slice(0, 6)}…).
                </div>
              </div>
              <div className="row" style={{ marginTop: 12, gap: 8 }}>
                <Btn onClick={() => void bkDownloadV3()} disabled={bkBusy || bkV3Pass.length < 8}>
                  {bkBusy ? t("settings.encrypting") : t("settings.download_keystore")}
                </Btn>
                <Btn variant="ghost" onClick={bkLock}>{t("settings.lock")}</Btn>
              </div>
            </>
          )}
        </div>

        <NotificationsCard />

        <DiscordCard />

        <div className="s-label">{t("settings.developer_mode_heading")}</div>
        <div className="card card-pad">
          <div className="kv" style={{ borderBottom: 0 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, color: "var(--ink)" }}>
                {t("settings.show_api_keys")}
              </div>
              <div className="help">
                {t("settings.developer_mode_help")}
              </div>
            </div>
            <Toggle
              on={devMode}
              onChange={(next) => {
                setDeveloperMode(next);
                setDevModeLocal(next);
              }}
              ariaLabel={t("settings.developer_mode_heading")}
            />
          </div>
        </div>

        <div className="s-label">{t("settings.support_heading")}</div>
        <div className="card card-pad">
          <p className="help" style={{ marginBottom: 12 }}>
            {t("settings.support_help")}
          </p>
          <div className="field">
            <label>{t("settings.account_id_label")}</label>
            <CopyField value={merchant.tg_user_id != null ? String(merchant.tg_user_id) : ""} />
          </div>
          {merchant.display_name ? (
            <div className="field" style={{ marginTop: 12 }}>
              <label>{t("settings.account_name_label")}</label>
              <CopyField value={merchant.display_name} />
            </div>
          ) : null}
        </div>

        <div className="s-label">{t("settings.status_heading")}</div>
        <div className="card card-pad">
          <KV k={t("settings.setup_verified_label")}>
            {merchant.setup_verified ? (
              <span className="pill ok"><span className="pd" />{t("settings.verified")}</span>
            ) : (
              <span className="pill warn"><span className="pd" />{t("settings.unverified")}</span>
            )}
          </KV>
          <KV k={t("settings.account_label")}>
            {merchant.enabled ? (
              <span className="pill ok"><span className="pd" />{t("settings.enabled")}</span>
            ) : (
              <span className="pill fail"><span className="pd" />{t("settings.disabled")}</span>
            )}
          </KV>
        </div>

        {getStoredSession() && (
          <>
            <div className="s-label">{t("settings.account_heading")}</div>
            <div className="card card-pad">
              <Btn
                variant="ghost"
                onClick={() => {
                  void signOut().then(() => {
                    void navigate({ to: "/login" });
                  });
                }}
                style={{ color: "var(--danger-ink)" }}
              >
                {t("settings.sign_out")}
              </Btn>
            </div>
          </>
        )}

        <div className="save-bar">
          <Btn block loading={busy} disabled={busy} onClick={() => void handleSave()}>
            {t("settings.save_changes")}
          </Btn>
        </div>
      </div>
    </div>
  );
}

/**
 * Discord linking — OAuth2 from the dashboard (identify scope only). Once
 * linked, the Shieldz Discord bot answers that user with this account's
 * numbers; the server only ever hands out read-only daily aggregates.
 */
type DiscordLink = {
  linked: boolean;
  discord_user_id: string | null;
  discord_username: string | null;
};

function DiscordCard() {
  const t = useT();
  const [link, setLink] = useState<DiscordLink | null>(null);
  const [dcBusy, setDcBusy] = useState(false);
  const [dcErr, setDcErr] = useState<string | null>(null);

  const refresh = async () => {
    try {
      setLink(await adminFetch<DiscordLink>("/api/v1/discord/link"));
    } catch (e) {
      setDcErr(e instanceof ApiError ? e.message : String(e));
    }
  };

  useEffect(() => {
    void refresh();
    // The OAuth round-trip leaves and comes back (or finishes in another
    // tab): refetch whenever the page becomes visible again.
    const onVis = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const connect = async () => {
    setDcBusy(true);
    setDcErr(null);
    try {
      const { url } = await adminFetch<{ url: string }>("/api/v1/discord/link/start", {
        method: "POST",
      });
      window.location.href = url;
    } catch (e) {
      setDcErr(e instanceof ApiError ? e.message : String(e));
      setDcBusy(false);
    }
  };

  const disconnect = async () => {
    setDcBusy(true);
    setDcErr(null);
    try {
      await adminFetch<{ linked: boolean }>("/api/v1/discord/link", { method: "DELETE" });
      await refresh();
    } catch (e) {
      setDcErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setDcBusy(false);
    }
  };

  return (
    <>
      <div className="s-label">{t("settings.discord_heading")}</div>
      <div className="card card-pad">
        <div className="kv" style={{ borderBottom: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, color: "var(--ink)" }}>
              {link?.linked
                ? `${t("settings.discord_connected_as")} ${link.discord_username ?? link.discord_user_id}`
                : t("settings.discord_connect_account")}
            </div>
            <div className="help">
              {link?.linked
                ? t("settings.discord_linked_help")
                : t("settings.discord_unlinked_help")}
            </div>
            {dcErr ? (
              <div className="help" style={{ color: "var(--danger-ink)", marginTop: 6 }}>
                {dcErr}
              </div>
            ) : null}
          </div>
          {link?.linked ? (
            <Btn variant="ghost" disabled={dcBusy} onClick={() => void disconnect()}>
              {dcBusy ? "…" : t("settings.disconnect")}
            </Btn>
          ) : (
            <Btn disabled={dcBusy} onClick={() => void connect()}>
              {dcBusy ? t("settings.opening") : t("settings.connect")}
            </Btn>
          )}
        </div>
      </div>
    </>
  );
}
